import { Button, Col, Drawer, Row, Select, Spin, message } from 'antd';
import { useEffect, useState, useCallback, useMemo } from 'react';
import { handlers } from '../../../../helpers/helpers';
import { getAsset } from '../../../../services/general';
import { signExpertByState, updateSignatureState, getFormSignature } from '../../../../services/exam';
import { LeftOutlined, RightOutlined, EyeOutlined } from '@ant-design/icons';
import config from '../../../../config';
import { useTranslation } from 'react-i18next';

const { Option } = Select;

export const hasDocumentFiles = (exam) =>
  exam?.viewableFiles?.length > 0
  || exam?.dicomFiles?.length > 0
  || !!exam?.documentResultId;

const DocumentPreviewDrawer = ({
  open,
  record,
  examsList,
  onClose,
  onNavigateExam,
  onOpenDetailDrawer,
  onSignExam,
  onRefreshData,
}) => {
  const { t } = useTranslation();
  const [signingLoading, setSigningLoading] = useState(false);
  const [viewableUrls, setViewableUrls] = useState([]);
  const [documentLoading, setDocumentLoading] = useState(false);
  const [activeFileIndex, setActiveFileIndex] = useState(0);
  const [selectedState, setSelectedState] = useState(undefined);
  const [stateChanged, setStateChanged] = useState(false);
  const [expertFields, setExpertFields] = useState([]);

  const examsWithDocuments = useMemo(() => {
    return (examsList || []).filter(hasDocumentFiles);
  }, [examsList]);
  const currentDocIndex = useMemo(() => {
    if (!record) return -1;
    return examsWithDocuments.findIndex(
      exam => exam.admission_patient_exam === record.admission_patient_exam
    );
  }, [record, examsWithDocuments]);

  const totalWithDocs = examsWithDocuments.length;
  const canNavigatePrevious = currentDocIndex > 0;
  const canNavigateNext = currentDocIndex < totalWithDocs - 1;

  const dicomFiles = record?.dicomFiles || [];

  const stateField = useMemo(() => {
    if (!expertFields?.length) return null;
    return expertFields.find(expertField => expertField.code?.startsWith('ESTADO_')) || null;
  }, [expertFields]);

  useEffect(() => {
    const fetchExpertFields = async () => {
      if (!record?.admission_patient_exam) return;
      try {
        const response = await getFormSignature({
          admissionPatientExamId: record.admission_patient_exam,
        });
        if (response?.expert_fields) {
          setExpertFields(response.expert_fields);
          const stateField = response.expert_fields.find(expertField => expertField.code?.startsWith('ESTADO_'));
          if (stateField) {
            setSelectedState(stateField.result || stateField.default_value || undefined);
          } else {
            setSelectedState(record.expertStatus || undefined);
          }
        }
      } catch (error) {
        console.error('Error fetching expert fields:', error);
        setSelectedState(record.expertStatus || undefined);
      }
      setStateChanged(false);
    };

    if (open && record) {
      fetchExpertFields();
    }
  }, [open, record]);

  useEffect(() => {
    const fetchViewableAssets = async () => {
      const files = record?.viewableFiles;
      if (!files || files.length === 0) {
        if (record?.documentResultId) {
          try {
            setDocumentLoading(true);
            const response = await getAsset({
              field: 'document',
              id: record.documentResultId,
              type: 'AdmissionProcedureResult',
            });
            setViewableUrls(response?.document_url
              ? [{ url: response.document_url, documentType: record.documentType || 'unknown' }]
              : []
            );
          } catch (error) {
            console.error('Error al cargar documento:', error);
            setViewableUrls([]);
          } finally {
            setDocumentLoading(false);
          }
        } else {
          setViewableUrls([]);
        }
        return;
      }

      try {
        setDocumentLoading(true);
        const results = await Promise.all(
          files.map(async (file) => {
            try {
              const response = await getAsset({
                field: 'document',
                id: file.documentResultId,
                type: 'AdmissionProcedureResult',
              });
              return {
                url: response?.document_url || null,
                documentType: file.documentType || 'unknown',
                code: file.code,
                codePrefix: file.codePrefix,
              };
            } catch (error) {
              console.error(`Error al cargar archivo ${file.code}:`, error);
              return null;
            }
          })
        );
        setViewableUrls(results.filter(result => result && result.url));
      } catch (error) {
        console.error('Error al cargar documentos:', error);
        setViewableUrls([]);
      } finally {
        setDocumentLoading(false);
      }
    };

    if (open && record) {
      setActiveFileIndex(0);
      fetchViewableAssets();
    } else {
      setViewableUrls([]);
      setActiveFileIndex(0);
    }
  }, [open, record]);

  const saveStateOnly = useCallback(async () => {
    if (!record || !stateChanged || !selectedState || !stateField) return;

    try {
      await updateSignatureState({
        admissionPatientExamId: record.admission_patient_exam,
        type: 'saved',
        body: {
          expertFields: [{
            id: stateField.id,
            input_type: stateField.input_type,
            result: selectedState,
          }],
          diagnostics: [],
        },
      });
      setStateChanged(false);
    } catch (error) {
      console.error('Error saving expert exam state:', error);
      message.error({
        content: `Error al guardar estado.`,
        style: {
          marginTop: '40vh'
        }
      });
    }
  }, [record, stateChanged, selectedState, stateField]);

  const navigateToDocExam = useCallback((docListIndex) => {
    if (docListIndex < 0 || docListIndex >= examsWithDocuments.length) return;
    const targetExam = examsWithDocuments[docListIndex];
    const fullIndex = (examsList || []).findIndex(
      exam => exam.admission_patient_exam === targetExam.admission_patient_exam
    );
    if (fullIndex >= 0 && onNavigateExam) {
      onNavigateExam(fullIndex);
    }
  }, [examsWithDocuments, examsList, onNavigateExam]);

  const handleDicomPreview = useCallback(async (dicomFile) => {
    try {
      const response = await getAsset({
        field: 'document',
        id: dicomFile.documentResultId,
        type: 'AdmissionProcedureResult',
      });
      if (response?.document_url) {
        const newWindow = window.open(
          `${config.dicomUrl}?url=${response.document_url}`,
          '_blank',
          'noopener,noreferrer'
        );
        if (newWindow) newWindow.opener = null;
      }
    } catch (error) {
      console.error('Error al abrir visor DICOM:', error);
      message.error({
        content: 'Error al abrir visor DICOM',
        className: 'signature-message',
      });
    }
  }, []);

  if (!record) return null;

  const formattedDocument = record.document_type === 'rut'
    ? handlers.formatDocument(record.document_number)
    : record.document_number;

  const totalViewable = viewableUrls.length;
  const currentFile = viewableUrls[activeFileIndex] || null;
  const currentFileUrl = currentFile?.url || null;
  const IMAGE_EXTENSIONS = /\.(jpe?g|png|gif|webp)(\?|$)/i;
  const isCurrentImage = currentFileUrl && IMAGE_EXTENSIONS.test(currentFileUrl);
  const hasViewable = totalViewable > 0;
  const hasDicom = dicomFiles.length > 0;

  const handleStateChange = (value) => {
    setSelectedState(value);
    setStateChanged(true);
  };

  const handleSignExam = async () => {
    if (!selectedState) {
      message.warning({
        content: 'Debe seleccionar un estado antes de firmar',
        className: 'signature-message',
      });
      return;
    }
    try {
      setSigningLoading(true);
      await signExpertByState({
        admissionPatientExamId: record.admission_patient_exam,
        stateValue: selectedState,
      });

      message.success({
        content: 'Examen firmado exitosamente',
        className: 'signature-message',
      });

      if (onRefreshData) {
        await onRefreshData();
      }

      if (onSignExam) {
        onSignExam(record, currentDocIndex);
      }
    } catch (error) {
      console.error('Error al firmar:', error);
      const errorCode = error?.code || error?.message;
      const errorMessage = t(`signature.errors.${errorCode}`, `Error al firmar examen: ${errorCode || error}`);
      message.error({
        content: errorMessage,
        className: 'signature-message',
      });
    } finally {
      setSigningLoading(false);
    }
  };

  const handleNavigatePrevious = async () => {
    if (stateChanged) {
      await saveStateOnly();
    }
    if (canNavigatePrevious) {
      navigateToDocExam(currentDocIndex - 1);
    }
  };

  const handleNavigateNext = async () => {
    if (stateChanged) {
      await saveStateOnly();
    }
    if (canNavigateNext) {
      navigateToDocExam(currentDocIndex + 1);
    }
  };

  const handleViewDetails = () => {
    if (onOpenDetailDrawer) {
      onOpenDetailDrawer(record);
    }
  };

  return (
    <Drawer
      className="document-preview-drawer"
      title={null}
      placement="right"
      width="74%"
      open={open}
      onClose={onClose}
      closable={true}
      destroyOnClose
    >
      <h2 className="document-preview-drawer__title">
        {`Resultado de ${record.exam_name || 'Examen'}`}
      </h2>

      <Row gutter={[20, 16]} align="top">
        <Col span={10}>
          <div className="document-preview-drawer__patient-card">
            <p>
              Trabajador: <strong>{record.full_name}</strong>
            </p>
            <p>
              DNI: <strong>{formattedDocument}</strong>
            </p>
            {record.age && (
              <p>
                Edad: <strong>{record.age}</strong>
              </p>
            )}
          </div>
        </Col>
        <Col span={14}>
          <div className="document-preview-drawer__status-section">
            <div className="document-preview-drawer__status-field">
              <p className="document-preview-drawer__status-label">Estado</p>
              {stateField ? (
                <Select
                  className="cmt-select document-preview-drawer__state-select"
                  value={selectedState}
                  placeholder="Sin estado"
                  onChange={handleStateChange}
                >
                  {stateField.options?.map((option, index) => (
                    <Option key={index} value={option}>
                      {option}
                    </Option>
                  ))}
                </Select>
              ) : null}
            </div>
          </div>
        </Col>
      </Row>

      <div className="document-preview-drawer__document-container">
        {documentLoading ? (
          <Spin size="large" />
        ) : hasViewable && currentFileUrl ? (
          isCurrentImage ? (
            <div className="document-preview-drawer__image-viewer">
              <img
                src={currentFileUrl}
                alt={`Resultado de ${record.exam_name}`}
                className="document-preview-drawer__image"
              />
            </div>
          ) : (
            <iframe
              src={`${currentFileUrl}#toolbar=1`}
              title={`Resultado de ${record.exam_name}`}
              loading="lazy"
              allow="fullscreen"
            />
          )
        ) : !hasViewable && hasDicom ? (
          <p className="document-preview-drawer__empty-dicom">
            Este examen solo tiene archivos DICOM. Use el botón de abajo para visualizarlos.
          </p>
        ) : (
          <p className="document-preview-drawer__empty-text">
            No hay documento disponible para este examen
          </p>
        )}
      </div>

      <div className="document-preview-drawer__actions-row">
        <div className="actions-left">
          {totalViewable > 1 && (
            <div className="document-preview-drawer__file-nav">
              <button
                className="file-nav-arrow"
                disabled={activeFileIndex === 0}
                onClick={() => setActiveFileIndex(index => index - 1)}
              >
                <LeftOutlined />
              </button>
              <span className="file-nav-current">{activeFileIndex + 1}</span>
              <span className="file-nav-separator">/</span>
              <span className="file-nav-total">{totalViewable}</span>
              <button
                className="file-nav-arrow"
                disabled={activeFileIndex === totalViewable - 1}
                onClick={() => setActiveFileIndex(index => index + 1)}
              >
                <RightOutlined />
              </button>
            </div>
          )}

          {hasDicom && (
            <Button
              className="cmt-button primary outline"
              type="primary"
              ghost
              icon={<EyeOutlined />}
              onClick={() => handleDicomPreview(dicomFiles[activeFileIndex] || dicomFiles[0])}
            >
              Visualizar DICOM
            </Button>
          )}
        </div>

        <Button
          className="cmt-button primary outline"
          type="primary"
          ghost
          onClick={handleViewDetails}
        >
          Ver detalles
        </Button>
      </div>

      <div className="document-preview-drawer__footer">
        <div className="footer-left">
          <Button
            className="document-preview-drawer__nav-button"
            disabled={!canNavigatePrevious}
            onClick={handleNavigatePrevious}
          >
            Anterior
          </Button>
          <Button
            className="document-preview-drawer__nav-button"
            disabled={!canNavigateNext}
            onClick={handleNavigateNext}
          >
            Siguiente
          </Button>
          <span className="footer-info">
            {currentDocIndex >= 0 ? currentDocIndex + 1 : 0} / {totalWithDocs}
          </span>
        </div>
        <Button
          className="document-preview-drawer__sign-button"
          type="primary"
          onClick={handleSignExam}
          loading={signingLoading}
          disabled={!selectedState}
        >
          Firmar
        </Button>
      </div>
    </Drawer>
  );
};

export default DocumentPreviewDrawer;
